'use client'

import { forwardRef, useId } from 'react'

import { cn } from '@/core/utils/common'

import type { InputHTMLAttributes, ReactNode } from 'react'

type SwitchSize = 'sm' | 'md'

type SwitchProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> & {
  label?: ReactNode
  size?: SwitchSize
  error?: string
}

const sizeConfig: Record<SwitchSize, { track: string; thumb: string }> = {
  sm: {
    track: 'h-5 w-9',
    thumb: 'size-4 peer-checked:translate-x-4',
  },
  md: {
    track: 'h-6 w-11',
    thumb: 'size-5 peer-checked:translate-x-5',
  },
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ label, size = 'md', error, className, id, ...props }, ref) => {
    const generatedId = useId()
    const inputId = id || props.name || generatedId
    const errorId = error ? `${inputId}-error` : undefined
    const sizeStyles = sizeConfig[size]

    return (
      <div>
        <label
          htmlFor={inputId}
          className={cn(
            'inline-flex cursor-pointer select-none items-center gap-2.5',
            props.disabled && 'cursor-not-allowed opacity-50',
            className
          )}
        >
          <span className="relative inline-flex items-center">
            <input
              ref={ref}
              type="checkbox"
              role="switch"
              id={inputId}
              className="peer sr-only"
              aria-invalid={Boolean(error)}
              aria-describedby={errorId}
              {...props}
            />
            <span
              className={cn(
                'block rounded-full bg-surface-strong transition-colors duration-200',
                'peer-checked:bg-point peer-focus-visible:bg-description',
                sizeStyles.track
              )}
            />
            <span
              className={cn(
                'absolute left-0.5 rounded-full bg-white shadow-[0_1px_3px_rgb(0_0_0/0.4)]',
                'transition-transform duration-200',
                sizeStyles.thumb
              )}
            />
          </span>
          {label && <span className="text-c14 text-foreground">{label}</span>}
        </label>
        {error && (
          <p
            id={errorId}
            role="alert"
            className="mt-2 text-c12 leading-5 text-error"
          >
            {error}
          </p>
        )}
      </div>
    )
  }
)

Switch.displayName = 'Switch'
